import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getContractVersions,
  getContractVersionById,
  createContractVersion,
  updateContractVersion,
  deleteContractVersion,
  submitContractVersion,
  approveContractVersion,
  rejectContractVersion,
  applyContractVersion,
  getActiveContractState,
  getContractStateAt,
} from "libs/api/contract_version";

function wrapThunk(type, apiFn) {
  return createAsyncThunk(type, async ({ cbPending, cbFulfilled, cbRejected, cbFinally, ...args }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await apiFn(args);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  });
}

export const getContractVersionsThunk = createAsyncThunk(
  "contractVersion/getAll",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, contractId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getContractVersions(contractId);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const getContractVersionByIdThunk = createAsyncThunk(
  "contractVersion/getById",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getContractVersionById(versionId);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const createContractVersionThunk = createAsyncThunk(
  "contractVersion/create",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, contractId, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await createContractVersion(contractId, body);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const updateContractVersionThunk = createAsyncThunk(
  "contractVersion/update",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updateContractVersion(versionId, body);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const deleteContractVersionThunk = createAsyncThunk(
  "contractVersion/delete",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await deleteContractVersion(versionId);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const submitContractVersionThunk = createAsyncThunk(
  "contractVersion/submit",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await submitContractVersion(versionId);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const approveContractVersionThunk = createAsyncThunk(
  "contractVersion/approve",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await approveContractVersion(versionId, body);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const rejectContractVersionThunk = createAsyncThunk(
  "contractVersion/reject",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await rejectContractVersion(versionId, body);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const applyContractVersionThunk = createAsyncThunk(
  "contractVersion/apply",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, versionId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await applyContractVersion(versionId);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const getActiveContractStateThunk = createAsyncThunk(
  "contractVersion/getActiveState",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, contractId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getActiveContractState(contractId);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);
